import { Permission, permissionSet } from '@all-hands-hq/plex-graphql-schema';
import { Button, ButtonGroup, createStyles, makeStyles, Theme } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import React, { useState } from 'react';

import {
  AfterUpsertAnswerAction,
  CardType,
  useCreateFactMutation,
  useGetBoardByIdQuery,
} from '../../../../graphql/gen/types';
import { usePublishStatusFilter } from '../../../../models/filters/usePublishStatusFilter';
import { useSelectedLabelIds } from '../../../../models/filters/useSelectedLabelIds';
import { spacings } from '../../../../theme';
import { SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { usePlexRoute } from '../../details/usePlexRoute';
import { canChangeAnswerApprovalStatus } from '../../permissions';
import { logAnalytics } from '../../shared/logAnalytics';
import { MoreOptionsMenu } from '../../shared/MoreOptionsMenu';
import { DialogActions } from '../DialogActions';
import { useDialogState } from '../useDialogState';
import { FactDialogContent, State } from './FactDialogContent';

type Props = {
  boardId: string;
  initialText?: string;
  type: CardType;
};

export function CreateFactDialogContent({ boardId, initialText, type }: Props): React.ReactElement {
  const classes = useStyles();
  const [, setDialogState] = useDialogState();
  const [, updatePlexRoute] = usePlexRoute();
  const filterPublishStatus = usePublishStatusFilter();
  const labelIds = useSelectedLabelIds();
  // TODO: error handling
  const [createFact, { loading: createLoading }] = useCreateFactMutation();
  const { data: boardData, loading: boardLoading } = useGetBoardByIdQuery({ variables: { boardId } });
  const [state, setState] = useState<State>({
    text: initialText || '',
    labelIds: [],
    boardId,
    answer: {
      body: SlateEditorValue.new(),
      entities: { expanded_link: [], mention: [] },
      primaryLinkId: null,
    },
  });

  const board = boardData?.loggedIn?.boardById;

  if (board == null) {
    return <Skeleton variant="text" />;
  }

  const plexRelation = boardData?.loggedIn?.boardById?.plex.currentUserRelation?.state;

  const canPublishAnswer =
    plexRelation != null &&
    canChangeAnswerApprovalStatus({ plexRelation }) &&
    permissionSet(plexRelation).has(Permission.CHANGE_QUESTION_PUBLISH_STATUS);

  const handleSave = ({ isDraft }: { isDraft: boolean }): void => {
    const { answer, ...rest } = state;

    const afterAction = canPublishAnswer ? AfterUpsertAnswerAction.ApproveAndPublish : AfterUpsertAnswerAction.None;

    const questionsAnswer =
      answer != null && !answer.body.isEmpty()
        ? {
            body: answer.body.value,
            primaryLinkId: answer.primaryLinkId,
            afterAction,
          }
        : undefined;

    createFact({
      variables: {
        question: {
          plexId: board.plexId,
          questionTemplateId: null,
          isDraft,
          answer: questionsAnswer,
          type,
          ...rest,
        },
        filterPublishStatus,
        labelIds,
      },
    }).then(({ data }) => {
      if (data?.createQuestion != null) {
        const { id, permalink } = data.createQuestion;
        setDialogState(null, 'replace');
        updatePlexRoute({ selectedItem: { type: 'question', permalink, commentsVisible: false } }, 'replace');
        logAnalytics({ name: 'faq created', details: { plexId: board.plexId, itemId: id } });
      }
    });
  };

  const handleCreate = (): void => {
    handleSave({ isDraft: false });
  };

  const handleCreateDraft = (): void => {
    handleSave({ isDraft: true });
  };

  const handleChange = (state: State): void => {
    setState(state);
  };

  // TODO: update title based on plex relation
  return (
    <div className={classes.root}>
      <FactDialogContent
        type={type}
        value={state}
        plexId={board.plex.id}
        plexName={board.plex.name}
        plexColor={board.plex.color}
        role={board.plex.currentUserRelation.state}
        onChange={handleChange}
        title={type === CardType.Faq ? 'Create FAQ' : ' Create Card'}
      />

      <DialogActions
        cta={
          <ButtonGroup disabled={boardLoading || createLoading}>
            <Button onClick={handleCreate} variant="contained" color="primary">
              Create
            </Button>
            <MoreOptionsMenu
              id={'new'}
              variant={'contained'}
              color={'primary'}
              size={'small'}
              options={[
                {
                  label: 'Create draft',
                  onClick: handleCreateDraft,
                },
              ]}
              Button={Button}
              menuProps={{
                marginThreshold: 0,
                anchorOrigin: { horizontal: 'right', vertical: 'top' },
                transformOrigin: { horizontal: 'right', vertical: 'bottom' },
              }}
            />
          </ButtonGroup>
        }
      />
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    plexName: {
      marginLeft: spacings.xlarge,
      marginTop: spacings.normal,
      marginBottom: spacings.normal,
    },
  })
);
